// A city's own additions to the shell on the world map, beside the trains
// its region runtime puts in the shared store: London's National Rail (its
// own trains, stations, cards and rows, apps/london/src/client/nr-ui.ts).
// The shell takes one set of extras per page; the world page gives it one
// that routes to each region's, and an extension runs only while its region
// polls (its stream open, its trains drawn and counted). Its line-list rows
// are listed from the start, in the region's ids, and shown with the
// region's other rows.

import type { LatLon, StationId } from '@tol/core/types';
import type { StationSection } from '@tol/ui/card';
import type { ExtraPick, ShellExtras, StationHit } from '@tol/ui/shell';
import { regionStates } from './viewport.ts';
import type { Box, Camera, RegionState } from './viewport.ts';

export interface RegionExtension {
  // The region's id; its rows' and lines' ids carry it as a prefix.
  readonly region: string;
  readonly extras: ShellExtras;
  // A section of the region's own station cards (London: National Rail's
  // departures at a TfL station).
  readonly stationSection?: (sid: StationId) => StationSection | null;
  // Static coverage and track are independent of the host region's. London's
  // National Rail extends well beyond TfL's extent.
  readonly extent: Box;
  readonly tracks: () => readonly Box[] | null;
  readonly load: () => Promise<void>;
  // The extension started or stopped polling.
  readonly start: () => void;
  readonly stop: () => void;
}

export function extensionStates(
  camera: Camera,
  extensions: readonly RegionExtension[],
  hidden = false,
): Map<string, RegionState> {
  return regionStates(
    camera,
    extensions.map((e) => ({ id: e.region, extent: e.extent, tracks: e.tracks() })),
    hidden,
  );
}

// The page's extras: every extension's rows; the running ones' everything else.
export function combineExtras(
  all: () => readonly RegionExtension[],
  running: () => readonly RegionExtension[],
): ShellExtras {
  const each = (): readonly ShellExtras[] => running().map((e) => e.extras);
  return {
    draw: (ctx, dt, scale, w, h) => {
      for (const x of each()) x.draw?.(ctx, dt, scale, w, h);
    },
    drawn: () => each().flatMap((x) => x.drawn?.() ?? []),
    pick: (pt, radius) => {
      let best: ExtraPick | null = null;
      for (const x of each()) {
        const p = x.pick?.(pt, radius) ?? null;
        if (p && (best === null || p.distance < best.distance)) best = p;
      }
      return best;
    },
    stationAt: (pt, radius) => {
      for (const x of each()) {
        const hit: StationHit | null = x.stationAt?.(pt, radius) ?? null;
        if (hit) return hit;
      }
      return null;
    },
    renderCard: (card, selection, full) => each().some((x) => x.renderCard?.(card, selection, full) === true),
    rosterRows: () => all().flatMap((e) => e.extras.rosterRows?.() ?? []),
    // Within the view's box when the shell asks for one (the in-view count).
    counts: (within) => {
      const got = each().map((x) => x.counts?.(within) ?? null);
      if (got.length > 0 && got.every((c) => c === null)) return null;
      const n = new Map<string, number>();
      for (const c of got) for (const [id, k] of c ?? []) n.set(id, k);
      return n;
    },
    onStationsVisible: (visible) => {
      for (const x of each()) x.onStationsVisible?.(visible);
    },
    followed: (selection) => {
      for (const x of each()) {
        const at: LatLon | null = x.followed?.(selection) ?? null;
        if (at) return at;
      }
      return null;
    },
  };
}
